import { getAll } from './db.js';

const digits = (s) => (s || '').replace(/\D/g, '');
const lc = (s) => (s || '').toLowerCase();

export async function searchFamilies(db, query) {
  const q = query.trim();
  if (!q) return [];
  const qLc = lc(q);
  const qDigits = digits(q);
  const families = await getAll(db, 'families');
  const people = await getAll(db, 'people');
  const byFamily = new Map();
  for (const p of people) {
    if (!byFamily.has(p.family_key)) byFamily.set(p.family_key, []);
    byFamily.get(p.family_key).push(p);
  }
  const out = [];
  for (const f of families) {
    const fields = [f.name, f.address, f.city, f.state, f.zip].map(lc).join(' ');
    const nameHit = fields.includes(qLc);
    const phoneHit = qDigits && digits(f.phone).includes(qDigits);
    const memberHit =
      qLc &&
      (byFamily.get(f.family_key) || []).find(
        (p) => lc(p.name).includes(qLc) || lc(p.first).includes(qLc) || lc(p.last).includes(qLc),
      );
    if (nameHit || phoneHit || memberHit) out.push({ family: f, matchedPerson: memberHit || null });
  }
  return out.slice(0, 50);
}

export async function progressForStation(db, station) {
  const families = (await getAll(db, 'families')).filter(
    (f) => f.eligible && (!station || f.mandal === station),
  );
  const handovers = await getAll(db, 'handovers');
  const handedKeys = new Set(
    handovers.filter((h) => !h.voided_at && h.local_status !== 'invalid').map((h) => h.family_key),
  );
  const handed = families.filter((f) => handedKeys.has(f.family_key)).length;
  return { families: families.length, handed, remaining: families.length - handed };
}

function csvCell(v) {
  if (v == null) return '""';
  const s = String(v).replace(/"/g, '""');
  return `"${s}"`;
}

function csvRow(vals) {
  return vals.map(csvCell).join(',');
}

export async function handoverLogCsv(db, { mandal, staff, from, to } = {}) {
  const all = await getAll(db, 'handovers');
  const rows = all
    .filter((h) => !h.voided_at)
    .filter((h) => !mandal || h.station === mandal)
    .filter((h) => !staff || h.staff_name === staff)
    .filter((h) => !from || h.created_at >= from)
    .filter((h) => !to || h.created_at <= to)
    .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  const header = csvRow(['id', 'family_key', 'collected_name', 'staff', 'station', 'created_at']);
  const body = rows.map((h) =>
    csvRow([h.id, h.family_key, h.collected_name, h.staff_name, h.station, h.created_at]),
  );
  return [header, ...body].join('\n');
}

export async function notCollectedCsv(db, { mandal } = {}) {
  const families = (await getAll(db, 'families')).filter(
    (f) => f.eligible && (!mandal || f.mandal === mandal),
  );
  const handedKeys = new Set(
    (await getAll(db, 'handovers'))
      .filter((h) => !h.voided_at && h.local_status !== 'invalid')
      .map((h) => h.family_key),
  );
  const remaining = families.filter((f) => !handedKeys.has(f.family_key));
  const header = csvRow([
    'family_key',
    'name',
    'address',
    'city',
    'zip',
    'mandal',
    'collector',
    'phone',
  ]);
  return [
    header,
    ...remaining.map((f) =>
      csvRow([f.family_key, f.name, f.address, f.city, f.zip, f.mandal, f.collector_name, f.phone]),
    ),
  ].join('\n');
}

export async function perMandalRows(db) {
  const families = (await getAll(db, 'families')).filter((f) => f.eligible);
  const handovers = (await getAll(db, 'handovers')).filter(
    (h) => !h.voided_at && h.local_status !== 'invalid',
  );
  const handedKeys = new Set(handovers.map((h) => h.family_key));
  const buckets = new Map();
  for (const f of families) {
    const b = buckets.get(f.mandal) || { mandal: f.mandal, families: 0, handed: 0 };
    b.families += 1;
    if (handedKeys.has(f.family_key)) b.handed += 1;
    buckets.set(f.mandal, b);
  }
  return [...buckets.values()]
    .map((b) => ({
      ...b,
      pct: b.families ? Math.round((100 * b.handed) / b.families) : 0,
    }))
    .sort((a, b) => a.pct - b.pct);
}

export async function perMandalCsv(db) {
  const rows = await perMandalRows(db);
  const header = csvRow(['mandal', 'families', 'handed', 'pct']);
  return [header, ...rows.map((r) => csvRow([r.mandal, r.families, r.handed, r.pct]))].join('\n');
}

export async function paceBuckets(db, minutes = 15) {
  const handovers = (await getAll(db, 'handovers')).filter((h) => !h.voided_at && h.created_at);
  const bucketMs = minutes * 60 * 1000;
  const map = new Map();
  for (const h of handovers) {
    const t = new Date(h.created_at).getTime();
    const key = Math.floor(t / bucketMs) * bucketMs;
    map.set(key, (map.get(key) || 0) + 1);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a - b)
    .map(([bucket, count]) => ({
      bucket: new Date(bucket).toISOString(),
      count,
    }));
}

export async function staffActivityRows(db) {
  const handovers = (await getAll(db, 'handovers')).filter((h) => !h.voided_at && h.staff_name);
  const map = new Map();
  for (const h of handovers) {
    const s = map.get(h.staff_name) || {
      name: h.staff_name,
      station: h.station,
      handed: 0,
      last_activity: null,
    };
    s.handed += 1;
    if (!s.last_activity || h.created_at > s.last_activity) s.last_activity = h.created_at;
    map.set(h.staff_name, s);
  }
  return [...map.values()].sort((a, b) => b.handed - a.handed);
}

export async function staffActivityCsv(db) {
  const rows = await staffActivityRows(db);
  const header = csvRow(['name', 'station', 'handed', 'last_activity']);
  return [header, ...rows.map((r) => csvRow([r.name, r.station, r.handed, r.last_activity]))].join(
    '\n',
  );
}
